// Single source of truth for the password policy. Imported by:
//   • the Zod request schemas in this package (registration, reset, admin-create)
//   • the API service layer to produce structured `error.code` values
//   • the web app to render inline hints
//
// Rule set (matches DOCS/architecture/auth-and-session.md §"Password policy"):
//   1. 8–20 characters
//   2. Contains at least one uppercase letter (A–Z)
//   3. Contains at least one special character (see SPECIAL_CHARS_RE)
//   4. No 3+ run of the same character (aaa, 111) and no 3+ ascending /
//      descending sequence (abc, 321, FED). Applies to letters and digits.
//   5. No 6-digit (YYMMDD / DDMMYY) or 8-digit (YYYYMMDD / MMDDYYYY) date
//      substring; if a `birthdate` context is supplied, the digits-only form
//      is also rejected as a substring.
//   6. No identifier fragment (email local part, username, displayName) of
//      length ≥ 4 may appear as a case-insensitive substring.
//
// Frontend MAY apply this entire validator client-side; server treats the
// result as authoritative regardless.
export const PASSWORD_MIN = 8;
export const PASSWORD_MAX = 20;
// Single source of allowed specials. Mirrored in DOCS/architecture/http-api.md.
export const SPECIAL_CHARS_RE = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/;
export const PASSWORD_POLICY_ERROR_CODES = {
    TooShort: 'AUTH_PASSWORD_TOO_SHORT',
    TooLong: 'AUTH_PASSWORD_TOO_LONG',
    MissingUppercase: 'AUTH_PASSWORD_MISSING_UPPERCASE',
    MissingSpecial: 'AUTH_PASSWORD_MISSING_SPECIAL',
    ConsecutiveRun: 'AUTH_PASSWORD_CONSECUTIVE_RUN',
    ContainsDate: 'AUTH_PASSWORD_CONTAINS_DATE',
    ContainsIdentifier: 'AUTH_PASSWORD_CONTAINS_IDENTIFIER',
};
const MESSAGES = {
    AUTH_PASSWORD_TOO_SHORT: `Password must be at least ${String(PASSWORD_MIN)} characters`,
    AUTH_PASSWORD_TOO_LONG: `Password must be at most ${String(PASSWORD_MAX)} characters`,
    AUTH_PASSWORD_MISSING_UPPERCASE: 'Password must include an uppercase letter',
    AUTH_PASSWORD_MISSING_SPECIAL: 'Password must include a special character',
    AUTH_PASSWORD_CONSECUTIVE_RUN: 'Password must not contain 3 or more repeated or sequential characters (e.g. aaa, abc, 123)',
    AUTH_PASSWORD_CONTAINS_DATE: 'Password must not contain a date or your birthdate',
    AUTH_PASSWORD_CONTAINS_IDENTIFIER: 'Password must not contain part of your email, username, or display name',
};
/** Run the policy. Returns null on success; otherwise the first failure
 *  encountered (rules are evaluated cheapest-first). */
export function validatePassword(password, ctx) {
    if (password.length < PASSWORD_MIN)
        return failure('AUTH_PASSWORD_TOO_SHORT');
    if (password.length > PASSWORD_MAX)
        return failure('AUTH_PASSWORD_TOO_LONG');
    if (!/[A-Z]/.test(password))
        return failure('AUTH_PASSWORD_MISSING_UPPERCASE');
    if (!SPECIAL_CHARS_RE.test(password))
        return failure('AUTH_PASSWORD_MISSING_SPECIAL');
    if (hasConsecutiveRun(password, 3))
        return failure('AUTH_PASSWORD_CONSECUTIVE_RUN');
    if (containsDatePattern(password))
        return failure('AUTH_PASSWORD_CONTAINS_DATE');
    if (ctx?.birthdate !== undefined && containsBirthdate(password, ctx.birthdate)) {
        return failure('AUTH_PASSWORD_CONTAINS_DATE');
    }
    if (ctx?.identifierFragments) {
        const lower = password.toLowerCase();
        for (const raw of ctx.identifierFragments) {
            const frag = raw.trim().toLowerCase();
            if (frag.length >= 4 && lower.includes(frag)) {
                return failure('AUTH_PASSWORD_CONTAINS_IDENTIFIER');
            }
        }
    }
    return null;
}
function failure(code) {
    return { code, message: MESSAGES[code] };
}
// --- helpers ---------------------------------------------------------------
function hasConsecutiveRun(pw, n) {
    if (pw.length < n)
        return false;
    for (let i = 0; i <= pw.length - n; i++) {
        const a = pw.charCodeAt(i);
        const b = pw.charCodeAt(i + 1);
        const c = pw.charCodeAt(i + 2);
        // Apply only to letters and digits — punctuation runs are allowed.
        if (!isAlphaNum(a) || !isAlphaNum(b) || !isAlphaNum(c))
            continue;
        if (a === b && b === c)
            return true;
        if (b - a === 1 && c - b === 1)
            return true;
        if (a - b === 1 && b - c === 1)
            return true;
    }
    return false;
}
function isAlphaNum(code) {
    return ((code >= 0x30 && code <= 0x39) || // 0–9
        (code >= 0x41 && code <= 0x5a) || // A–Z
        (code >= 0x61 && code <= 0x7a) //   a–z
    );
}
// 6-digit (YYMMDD / DDMMYY) or 8-digit (YYYYMMDD / MMDDYYYY) date.
// We deliberately do NOT reject a bare 4-digit year — that would be too
// aggressive (e.g. P@ssw2024 is fine; P@ss20240115 is not).
const DATE_REGEXES = [
    /(\d{2})(0[1-9]|1[012])(0[1-9]|[12]\d|3[01])/,
    /(0[1-9]|[12]\d|3[01])(0[1-9]|1[012])(\d{2})/,
    /(?:19|20)\d{2}(0[1-9]|1[012])(0[1-9]|[12]\d|3[01])/,
    /(0[1-9]|1[012])(0[1-9]|[12]\d|3[01])(?:19|20)\d{2}/,
];
function containsDatePattern(pw) {
    return DATE_REGEXES.some((re) => re.test(pw));
}
function containsBirthdate(pw, birthdate) {
    const digits = birthdate.replace(/\D/g, '');
    if (digits.length < 6)
        return false;
    // Match the full digits string and common rotations.
    if (pw.includes(digits))
        return true;
    if (digits.length >= 8 && pw.includes(digits.slice(2)))
        return true; // drop century
    if (digits.length >= 8 && pw.includes(digits.slice(0, 4)))
        return true; // year only
    return false;
}
//# sourceMappingURL=password-policy.js.map